import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Navigate } from 'react-router-dom'
import {
  useDocumentData,
  useDocumentDataOnce
} from "react-firebase-hooks/firestore";
import {
  collection,
  doc,
  getDoc,
  getFirestore,
  query,
} from "firebase/firestore";
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  getQuestionInfo, isEditorActive
} from '../utils/PSetUtil'
import SplitPane from 'react-split-pane';
import Markdown from '../components/rendering/Markdown.js'
import { FinalAnswer } from '../pset/components/FinalAnswer';
import { AnswerExplanation } from '../pset/components/AnswerExplanation';
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import {
  Button, Breadcrumb
} from 'react-bootstrap';
import { GradingPane } from './components/GradingPane.js'
import { ABRubric } from './components/rubric.js'
import Swal from 'sweetalert2';
import { GradingAnswerExplanation } from './components/GradingAnswerExplanation';
import { UserContext, useUser } from 'contexts/UserContext';
import { AboveAndBeyondPane } from './components/AboveAndBeyondPane';

export const GradingStudent = (props) => {

  let { qId, qtrId, psetId } = useParams();
  const { userMetaData, userMetaDataLoading } = useContext(UserContext)
  const user = useUser()

  // get public pset data
  var [publicPsetData, publicPsetLoading, publicPsetLoadingErr] = useDocumentDataOnce(
    doc(getFirestore(), `psets/${qtrId}/${psetId}/public`)
  );

  var [classData, classLoading, classLoadingErr] = useDocumentDataOnce(
    doc(getFirestore(), `class/${qtrId}/`)
  );

  // needed to find the next student ungraded
  var [gradebook, gradebookLoading, gradebookLoadingErr] = useDocumentData(
    doc(getFirestore(), `/psets/${qtrId}/${psetId}/private/gradebook/${qId}`)
  );



  if (publicPsetLoading || classLoading || gradebookLoading || userMetaDataLoading) {
    return <></>
  }


  if (publicPsetLoadingErr) {
    return <>Server error. Perhaps you have insufficient permissions </>
  }

  // make "fake" above and beyond question for grading
  let questionInfo;
  let questionType;
  
  if (qId === 'above-and-beyond') {
    questionType = 'aboveAndBeyond';
    questionInfo = {
      qId: 'above-and-beyond',
      type: 'aboveAndBeyond',
      title: 'Above and Beyond',
      prompt: 'Above and Beyond Project'
    };
  } else {
    questionInfo = getQuestionInfo(qId, publicPsetData.questionInfo);
    questionType = questionInfo?.type;
  }

  return <GradingStudentWithData
    {...props}
    questionType={questionType}
    questionInfo={questionInfo}
    publicPsetData={publicPsetData}
    gradebook={gradebook}
    classData={classData}
    qId={qId}
    psetId={psetId}
    qtrId={qtrId}
    userMetaData={userMetaData}
    user={user}
  />

}

const GradingStudentWithData = (props) => {
  let { studentId } = useParams();

  if (props.questionType !== 'aboveAndBeyond') {
    return (
      <GradingStudentLayout
        {...props}
        studentId={studentId}
        showQuestionPane={true} // always show question pane for regular questions
        attachedQuestionId={null}
      />
    );
  }

  // render the above and beyond data for grading
  return (
    <AboveAndBeyondSubmission
      {...props}
      studentId={studentId}
    />
  );
}

// this is a wrapper component that will render AB GRADING pane using the submission reference
const AboveAndBeyondSubmission = (props) => {
  const answerPath = `users/${props.studentId}/${props.qtrId}/${props.psetId}/answers/aboveAndBeyond`;
  const [answerData] = useDocumentDataOnce(doc(getFirestore(), answerPath));

  const submittedProjectId = answerData?.submittedProjectId;
  const projectPath = submittedProjectId
    ? `users/${props.studentId}/${props.qtrId}/${props.psetId}/projects/${submittedProjectId}`
    : null;

  if (!projectPath) {
    return (
      <GradingStudentLayout
        {...props}
        showQuestionPane={false}
        attachedQuestionId={null}
      />
    );
  }

  return (
    <AboveAndBeyondProjectData
      {...props}
      projectPath={projectPath}
    />
  );
};

// this is a wrapper component that is used to fetch AB project DATA
const AboveAndBeyondProjectData = (props) => {
  const [projectData] = useDocumentDataOnce(doc(getFirestore(), props.projectPath));

  // if project is attached to a question, get the question id to render the original prompt/solution
  const attachedQuestionId = projectData?.specificProject && projectData?.selectedQuestion
    ? projectData.selectedQuestion
    : null;
  const showQuestionPane = attachedQuestionId !== null; // this could be false for AB, but will always be true for regular questions

  return (
    <GradingStudentLayout
      {...props}
      showQuestionPane={showQuestionPane}
      attachedQuestionId={attachedQuestionId}
    />
  );
};

const GradingStudentLayout = (props) => {
  const { showQuestionPane, attachedQuestionId, studentId } = props;

  return (
    <SplitPane overflow="auto" split="horizontal" allowResize={false} primary="second" defaultSize={54}>
      {showQuestionPane ? (
        <SplitPane size={'33%'} minSize={2}>
          <QuestionPane {...props} attachedQuestionId={attachedQuestionId} />
          <AnswerAndGrading {...props} studentId={studentId} />
        </SplitPane>
      ) : (
        <div style={{ height: '100%' }}>
          <AnswerAndGrading {...props} studentId={studentId} />
        </div>
      )}
      <BottomNav {...props} />
    </SplitPane>
  );
};

const QuestionPane = (props) => {
  // if AB project is attached to a question, get the question id to render the original prompt/solution
  const questionId = props.attachedQuestionId || props.qId;
  let solutionPath = `/psets/${props.qtrId}/${props.psetId}/private/answers/${questionId}`;
  let question = getQuestionInfo(questionId, props.publicPsetData.questionInfo);
  let prompt = question?.prompt || '';
  // warning: tiptap questions save the answer in the content field
  return (
    <div className="d-flex flex-column" style={{ height: 'calc(100%)' }}>
      <Tabs defaultActiveKey="prompt" id="questionTabs">
        <Tab eventKey="prompt" title={<span style={{ color: 'black' }}>Prompt</span>}>
          <div className='p-2'>
            <b>Prompt: {question?.title || 'Untitled'}</b>
            <Markdown text={prompt}></Markdown>
          </div>
        </Tab>

      <Tab eventKey="soln" title={<span style={{ color: 'black' }}>Solution</span>}>
        <div className='p-2 answerTab'>
          <GradingAnswerExplanation {...props}
            editable={true}
            explanationPath={solutionPath}
            collaborative={true}
          />
        </div>
      </Tab>
    </Tabs>
  </div>
  )
}

const AnswerAndGrading = (props) => {
  const studentId = props.studentId;
  return (
    <SplitPane size={'50%'}>
      <div className="d-flex flex-column" style={{ height: 'calc(100%)' }}>
        <Tabs defaultActiveKey="prompt" id="questionTabs">
          <Tab eventKey="prompt" title={<span style={{ color: 'black' }}>Student Answer</span>}>
            <div className='p-2 answerTab'>
              <AnswerPane {...props} studentId={studentId} />
            </div>
          </Tab>
        </Tabs>
      </div>
      <GradingPaneTab {...props} studentId={studentId} />
    </SplitPane>
  );
}

const AnswerPane = (props) => {
  return <AnswerPaneWithData {...props}
  />
}

const AnswerPaneWithData = (props) => {
  // render above and beyond project pane instead of answer + answer explanation pane
  if (props.questionType === 'aboveAndBeyond') {
    return <AboveAndBeyondPane {...props} />;
  }
  
  // default
  let explanationPath = `users/${props.studentId}/${props.qtrId}/${props.psetId}/answers/${props.qId}`;
  console.log(explanationPath);
  return (
    <>
      <FinalAnswer {...props} user={{ uid: props.studentId }} />
      <GradingAnswerExplanation
        {...props}
        editable={false}
        explanationPath={explanationPath}
      />
    </>
  );
}

const GradingPaneTab = (props) => {
  // use ABRubric for above and beyond questions, undefined will default to the normal rubric
  const rubricToUse = props.questionType === 'aboveAndBeyond' ? ABRubric : undefined;
  return <div className="d-flex flex-column" style={{ height: 'calc(100%)' }}>
    <Tabs defaultActiveKey="grading" id="gradingTabs">
      <Tab eventKey="grading" title={<span style={{ color: 'black' }}>Grading</span>}>
        <div className='p-2 answerTab'>
          <GradingPane {...props} editing={true} rubric={rubricToUse} />
        </div>
      </Tab>
    </Tabs>
  </div>

}

const BottomNav = (props) => {
  let { studentId } = useParams();
  let currIndex = getCurrStudentIndex(props, studentId)
  let nextId = getNextStudent(props, studentId)
  let previousId = getPreviousStudent(props, studentId)
  var mainButtonDisabled = true//this.props.status != 'grading' || !this.props.isFilled
  var navDisabed = false
  let mainButtonText = 'Edit Grade'
  let submitText = 'Submit Grade' //this.props.isGradeSubmitted ? 'Resubmit Grade' : 'Submit Grade'
  const navigate = useNavigate();

  // onKeyDown handler function
  const keyDownHandler = (event) => {
    // be careful that the text box isn't active

    if (!isEditorActive()) {
      let keyCode = event.code
      if (keyCode == 'ArrowRight') {
        if (nextId) {
          navigate(`/grading/${props.qtrId}/${props.psetId}/${props.qId}/${nextId}`);
        }

      }
      if (keyCode == 'ArrowLeft') {
        if (previousId) {
          navigate(`/grading/${props.qtrId}/${props.psetId}/${props.qId}/${previousId}`)
        }

      }
    }
  };

  // handle keyboard events
  useEffect(() => {
    // only run this code once
    document.addEventListener("keydown", keyDownHandler, false);
    // return the destructor
    return () => {
      document.removeEventListener("keydown", keyDownHandler, false)
    }
  }, [nextId, previousId])

  const qtrId = props.qtrId
  const psetId = props.psetId
  const qId = props.qId

  return (
    <div className="bottomNav mt-10">
      
      <div style={{marginLeft:10}}>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">Grading</li>
          {/* <li className="breadcrumb-item"><a href={`/grading/${qtrId}`}>{qtrId}</a></li> */}
          <li className="breadcrumb-item"><a href={`/grading/${qtrId}/${psetId}`}>{psetId}</a></li>
          <li className="breadcrumb-item"><a href={`/grading/${qtrId}/${psetId}/${qId}`}>{qId}</a></li>
          <li className="breadcrumb-item"><a href={`/grading/${qtrId}/${psetId}/${qId}/${studentId}`}>{studentId}</a></li>
        </ol>
      </div>
        <div className="problemNavButtons">

          <Link
            className={'btn btn-info ' + (previousId == null ? ' disabled' : '')}
            style={{ marginRight: '5px' }}
            to={`/grading/${props.qtrId}/${props.psetId}/${props.qId}/${previousId}`}
            onClick={(event) => { if (previousId == null) event.preventDefault() }}
          >
            <i className="fa fa-angle-left" /> Previous
          </Link>
          <Link
            className="btn mr-2 btn-warning"
            disabled={mainButtonDisabled}
            style={{ marginRight: '5px' }}
            onClick={() => props.onMainButton(mainButtonText)}
          >{mainButtonText}</Link>
          <Link
            className={'btn btn-info ' + (nextId == null ? ' disabled' : '')}
            style={{ marginRight: '5px' }}
            onClick={(event) => { if (nextId == null) event.preventDefault() }}
            to={`/grading/${props.qtrId}/${props.psetId}/${props.qId}/${nextId}`}
          >
            Next <i className="fa fa-angle-right" />
          </Link>
          <button
            className={'btn btn-info ' + (nextId == null ? ' disabled' : '')}
            style={{ marginRight: '5px' }}
            onClick={() => {
              let nextUngraded = getNextUngraded(props.classData, props.gradebook)
              if (nextUngraded != null) {
                navigate(`/grading/${props.qtrId}/${props.psetId}/${props.qId}/${nextUngraded}`);
              } else {
                Swal.fire({
                  toast: true,
                  icon: 'success',
                  title: 'All done!',
                  showConfirmButton: true,
                });
              }
            }}
          >
            Next Ungraded<i className="fa fa-angle-right" />
          </button>
          <button
            className={'btn btn-info ' + (nextId == null ? ' disabled' : '')}
            style={{ marginRight: '5px' }}
            onClick={() => {
              let nextUnverified = getNextUnverified(props.classData, props.gradebook)
              if (nextUnverified != null) {
                navigate(`/grading/${props.qtrId}/${props.psetId}/${props.qId}/${nextUnverified}`);
              } else {
                Swal.fire({
                  toast: true,
                  icon: 'success',
                  title: 'All done!',
                  showConfirmButton: true,
                });
              }
            }}
          >
            Next Unverified<i className="fa fa-angle-right" />
          </button>
        </div>
      </div>
  );
}

function getNextUnverified(classData, gradebook) {
  for (const student of classData['students']) {
    let studentId = student.uid
    if (!studentId in gradebook) {
      return student.uid
    }
    if (!gradebook[studentId]) {
      return student.uid
    }
    if (gradebook[studentId].grader == 'auto') {
      return student.uid
    }

  }
  return null;
}

function getNextUngraded(classData, gradebook) {
  for (const student of classData['students']) {
    
    let studentId = student.uid
    if (!(studentId in gradebook)) {
      return studentId
    }
    if (!gradebook[studentId]) {
      return studentId
    }
    
    // if the max score is 0, then it's ungraded
    const maxPoints = gradebook[studentId].maxPoints
    if (maxPoints == 0) {
      return studentId
    }

  }
  return null;
}

function getCurrStudentIndex(props, currStudentId) {
  let studentList = props.classData.students
  for (let index = 0; index < studentList.length; index++) {
    const student = studentList[index];
    if (student.uid == currStudentId) {
      return index + 1
    }
  }
  console.error('could not find student ', currStudentId)
  return null
}

function getNextStudent(props, currStudentId) {
  let studentList = props.classData.students
  for (let index = 0; index < studentList.length; index++) {
    const student = studentList[index];
    if (student.uid == currStudentId) {
      if (index + 1 < studentList.length) {
        return studentList[index + 1].uid
      } else {
        // end of the list
        return null
      }
    }
  }
  console.error('could not find student ', currStudentId)
  return null
}

function getPreviousStudent(props, currStudentId) {
  let studentList = props.classData.students
  for (let index = 0; index < studentList.length; index++) {
    const student = studentList[index];
    if (student.uid == currStudentId) {
      if (index != 0) {
        return studentList[index - 1].uid
      } else {
        // start of the list
        return null
      }
    }
  }
  console.error('could not find student ', currStudentId)
  return null
}
